import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FiTrash2 } from "react-icons/fi";
import AdminLayout from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import ConfirmDeleteModal from "../../components/modal/Delete";

export default function DataPengguna() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://backend.smpn1kamal.sch.id/api/data-pengguna", {
        headers: { Authorization: `Bearer ${token}` },
        params: roleFilter ? { role: roleFilter } : {},
      });
      setUsers(res.data.data);
      setFilteredUsers(res.data.data);
    } catch {
      toast.error("Gagal mengambil data pengguna");
    } finally {
      setLoading(false);
    }
  }, [token, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    const keyword = search.toLowerCase();
    setFilteredUsers(
      users.filter((u) =>
        u.username?.toLowerCase().includes(keyword) ||
        u.email?.toLowerCase().includes(keyword) ||
        u.role?.toLowerCase().includes(keyword)
      )
    );
  }, [search, users]);

  const handleUpdateRole = async (id, newRole) => {
    try {
      await axios.put(
        `https://backend.smpn1kamal.sch.id/api/data-pengguna/${id}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Role berhasil diperbarui");
      fetchUsers();
    } catch {
      toast.error("Gagal mengubah role");
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(
        `https://backend.smpn1kamal.sch.id/api/data-pengguna/${userToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Pengguna berhasil dihapus");
      fetchUsers();
      setIsDeleteModalOpen(false);
    } catch {
      toast.error("Gagal menghapus pengguna");
    } finally {
      setIsDeleting(false);
      setUserToDelete(null);
    }
  };

  return (
    <AdminLayout>
      {/* HEADER */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-blue-600">Data Pengguna</h1>
        <p className="text-gray-500">SMPN 1 Kamal</p>
      </div>

      {/* CARD */}
      <div className="bg-white rounded-xl shadow p-5">
        <div className="flex flex-row justify-between items-center gap-2 mb-6">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="flex-1 md:flex-none border rounded-full px-3 py-2 text-[11px] md:text-sm md:w-48 bg-blue-50 border-blue-200 focus:ring-2 focus:ring-blue-400 outline-none"
          >
            <option value="">Semua Role</option>
            <option value="guru">Guru</option>
            <option value="pimpinan">Pimpinan</option>
            <option value="admin">Admin</option>
          </select>
          <input
            type="text"
            placeholder="Cari..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 md:flex-none border rounded-full px-4 py-2 text-[11px] md:text-sm md:w-72 focus:ring-2 focus:ring-blue-400 outline-none"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* DESKTOP */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left w-16">NO</th>
                    <th className="px-4 py-3 text-left">USERNAME</th>
                    <th className="px-4 py-3 text-left">EMAIL</th>
                    <th className="px-4 py-3 text-left">ROLE</th>
                    <th className="px-4 py-3 text-center">AKSI</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((u, i) => (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                      <td className="px-4 py-3 font-medium">{u.username}</td>
                      <td className="px-4 py-3 text-gray-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                            u.role === "admin"
                              ? "bg-purple-50 text-purple-600 border-purple-200"
                              : u.role === "guru"
                              ? "bg-green-50 text-green-600 border-green-200"
                              : "bg-orange-50 text-orange-600 border-orange-200"
                          }`}
                        >
                          <option value="guru">Guru</option>
                          <option value="pimpinan">Pimpinan</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => { setUserToDelete(u); setIsDeleteModalOpen(true); }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MOBILE */}
            <div className="md:hidden space-y-4">
              {filteredUsers.map((u) => (
                <div key={u.id} className="bg-slate-50 border border-gray-100 rounded-xl p-4 space-y-4">
                  <div>
                    <p className="font-bold text-lg text-gray-800">{u.username}</p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </div>
                  <div className="flex flex-row items-center gap-3">
                    <select
                      value={u.role}
                      onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                      className="flex-1 px-4 py-2 rounded-full text-sm bg-white border border-gray-200 outline-none focus:ring-2 focus:ring-blue-400 appearance-none text-center"
                    >
                      <option value="guru">Guru</option>
                      <option value="pimpinan">Pimpinan</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      onClick={() => { setUserToDelete(u); setIsDeleteModalOpen(true); }}
                      className="flex-1 bg-red-500 text-white py-2 px-4 rounded-full text-sm font-medium hover:bg-red-600 transition-colors shadow-sm"
                    >
                      Hapus
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredUsers.length === 0 && (
              <div className="text-center py-10 text-gray-400 italic font-medium">
                Data tidak ditemukan
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        title="Hapus Pengguna"
        description={`Apakah Anda yakin ingin menghapus pengguna "${userToDelete?.username}"? Seluruh data yang terkait dengan akun ini akan hilang.`}
      />
    </AdminLayout>
  );
}